import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPresentationCohortInjectionCandidates,
  pollPresentationAiJob,
  postCohortPresentation,
  postPresentationAiGenerate,
  postTeacherPresentation,
  type CoursePlanItem,
  type PresentationAiDraft,
  type PresentationCohortInjectionCandidate,
  type TeacherCohortAssignmentRecord,
  type TeacherPresentationSlide,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { AI_SPEND_TOOLTIP, AiSpendIcon } from "./ai-spend";
import LessonAiProgressPanel from "./LessonAiProgressPanel";
import { startLessonAiProgressClock, type LessonAiProgressUpdate } from "./lesson-ai-progress";
import {
  defaultPresentationTitle,
  followingLessonTitle,
  resolvePresentationTitle,
} from "./presentationDefaultTitle";
import { assignmentIdAfterPlanItem } from "./quizPlanInsert";

function newId(): string {
  return crypto.randomUUID();
}

type AddContext = "cohort" | "template";
type ModalPhase = "form" | "ai";

export interface AddPresentationModalProps {
  readonly open: boolean;
  readonly context: AddContext;
  readonly courseId: string;
  readonly cohortId?: string;
  readonly insertAfterPlanItemId: string | null | undefined;
  readonly planItems: readonly CoursePlanItem[];
  readonly assignments?: readonly TeacherCohortAssignmentRecord[];
  readonly onClose: () => void;
  readonly onAdded: () => void | Promise<void>;
}

export function AddPresentationModal({
  open,
  context,
  courseId,
  cohortId,
  insertAfterPlanItemId,
  planItems,
  assignments = [],
  onClose,
  onAdded,
}: AddPresentationModalProps) {
  const navigate = useNavigate();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const progressStopRef = useRef<(() => void) | null>(null);
  const cancelledRef = useRef(false);
  const [phase, setPhase] = useState<ModalPhase>("form");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [guidance, setGuidance] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiProgress, setAiProgress] = useState<LessonAiProgressUpdate | null>(null);
  const [injectionLoading, setInjectionLoading] = useState(false);
  const [injectionCandidates, setInjectionCandidates] = useState<readonly PresentationCohortInjectionCandidate[]>(
    []
  );
  const [selectedCohortIds, setSelectedCohortIds] = useState<ReadonlySet<string>>(() => new Set());

  const suggestedTitle = useMemo(
    () => defaultPresentationTitle(planItems, insertAfterPlanItemId),
    [planItems, insertAfterPlanItemId]
  );
  const focusLessonName = useMemo(
    () => followingLessonTitle(planItems, insertAfterPlanItemId),
    [planItems, insertAfterPlanItemId]
  );
  const insertAfterAssignmentId = useMemo(
    () => assignmentIdAfterPlanItem(insertAfterPlanItemId, planItems, assignments),
    [insertAfterPlanItemId, planItems, assignments]
  );

  const injectCohortIds = useMemo(() => {
    if (context !== "template" || selectedCohortIds.size === 0) return undefined;
    return [...selectedCohortIds];
  }, [context, selectedCohortIds]);

  const injectionAllSelected =
    injectionCandidates.length > 0 && selectedCohortIds.size === injectionCandidates.length;

  function toggleCohortInjection(id: string, checked: boolean) {
    setSelectedCohortIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleAllCohortInjection(checked: boolean) {
    if (!checked) {
      setSelectedCohortIds(new Set());
      return;
    }
    setSelectedCohortIds(new Set(injectionCandidates.map((c) => c.cohortId)));
  }

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPhase("form");
      setTitle("");
      setDescription("");
      setGuidance("");
      setError(null);
      setAiProgress(null);
      progressStopRef.current?.();
      progressStopRef.current = null;
      setInjectionLoading(false);
      setInjectionCandidates([]);
      setSelectedCohortIds(new Set());
    }
  }, [open]);

  useEffect(() => {
    if (!open || context !== "template" || !bearer) {
      setInjectionCandidates([]);
      setSelectedCohortIds(new Set());
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      setInjectionLoading(true);
      void getPresentationCohortInjectionCandidates(bearer, courseId)
        .then((data) => {
          if (cancelled) return;
          setInjectionCandidates(data.candidates);
          setSelectedCohortIds(new Set(data.candidates.map((c) => c.cohortId)));
        })
        .catch(() => {
          if (cancelled) return;
          setInjectionCandidates([]);
          setSelectedCohortIds(new Set());
        })
        .finally(() => {
          if (!cancelled) setInjectionLoading(false);
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [bearer, context, courseId, open]);

  const handleBackdropClick = useCallback(
    (e: MouseEvent<HTMLDialogElement>) => {
      if (e.target === dialogRef.current && !busy) onClose();
    },
    [busy, onClose]
  );

  const finishCohortCreate = useCallback(async () => {
    await onAdded();
    onClose();
  }, [onAdded, onClose]);

  const finishCourseCreate = useCallback(
    async (presentationId: string) => {
      await onAdded();
      onClose();
      navigate(
        `/teacher/courses/${encodeURIComponent(courseId)}/presentations/${encodeURIComponent(presentationId)}`
      );
    },
    [courseId, navigate, onAdded, onClose]
  );

  function payload(
    resolvedTitle: string,
    slides: TeacherPresentationSlide[],
    extras?: { creationPrompt?: string | null; iconName?: string; iconBgColor?: string; iconFgColor?: string }
  ) {
    return {
      title: resolvedTitle,
      description: description.trim() || null,
      creationPrompt: extras?.creationPrompt ?? null,
      slides,
      iconName: extras?.iconName ?? "book-open",
      iconBgColor: extras?.iconBgColor ?? "#1b4d2e",
      iconFgColor: extras?.iconFgColor ?? "#fdf9f3",
    };
  }

  async function handleCreateEmpty() {
    if (!bearer) return;
    const resolvedTitle = resolvePresentationTitle(title, planItems, insertAfterPlanItemId);
    setBusy(true);
    setError(null);
    try {
      const slides: TeacherPresentationSlide[] = [{ id: newId(), title: "New slide", body: [] }];
      if (context === "cohort" && cohortId) {
        await postCohortPresentation(bearer, cohortId, {
          ...payload(resolvedTitle, slides),
          insertAfterAssignmentId,
        });
        await finishCohortCreate();
      } else {
        const { presentation } = await postTeacherPresentation(bearer, courseId, {
          ...payload(resolvedTitle, slides),
          insertAfterPlanItemId,
          injectCohortIds,
        });
        await finishCourseCreate(presentation.id);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create presentation");
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    if (!bearer) return;
    const resolvedTitle = resolvePresentationTitle(title, planItems, insertAfterPlanItemId);
    setPhase("ai");
    setBusy(true);
    setError(null);
    progressStopRef.current?.();
    progressStopRef.current = startLessonAiProgressClock("presentation-generate", setAiProgress);
    cancelledRef.current = false;
    try {
      const trimmedGuidance = guidance.trim();
      const { jobId } = await postPresentationAiGenerate(bearer, courseId, {
        prompt: trimmedGuidance,
        insertAfterPlanItemId,
      });
      const draft: PresentationAiDraft = await pollPresentationAiJob(bearer, courseId, jobId);
      if (cancelledRef.current) return;
      const slides: TeacherPresentationSlide[] = draft.slides.map((s) => ({
        id: newId(),
        title: s.title,
        body: [...s.body],
        ...(s.code ? { code: s.code } : {}),
        ...(s.notes ? { notes: s.notes } : {}),
        ...(s.topicTags ? { topicTags: [...s.topicTags] } : {}),
      }));
      const aiTitle = draft.title?.trim() || resolvedTitle;
      const aiPayload = payload(aiTitle, slides, {
        creationPrompt: trimmedGuidance || null,
        iconName: draft.iconName,
        iconBgColor: draft.iconBgColor,
        iconFgColor: draft.iconFgColor,
      });
      if (context === "cohort" && cohortId) {
        await postCohortPresentation(bearer, cohortId, {
          ...aiPayload,
          insertAfterAssignmentId,
        });
        if (cancelledRef.current) return;
        await finishCohortCreate();
        return;
      }
      const { presentation } = await postTeacherPresentation(bearer, courseId, {
        ...aiPayload,
        insertAfterPlanItemId,
        injectCohortIds,
      });
      if (cancelledRef.current) return;
      await finishCourseCreate(presentation.id);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e.message : "Could not create presentation");
      setPhase("form");
    } finally {
      progressStopRef.current?.();
      progressStopRef.current = null;
      setAiProgress(null);
      setBusy(false);
    }
  }

  function cancelGeneration() {
    cancelledRef.current = true;
    progressStopRef.current?.();
    progressStopRef.current = null;
    setAiProgress(null);
    setPhase("form");
    setBusy(false);
  }

  return (
    <dialog ref={dialogRef} className="teacher-modal" onClose={onClose} onClick={handleBackdropClick}>
      <form method="dialog" className="teacher-modal-card" role="document" onSubmit={(e) => e.preventDefault()}>
        <h2 className="teacher-modal-title">Add presentation</h2>
        <p className="teacher-modal-lede">
          {focusLessonName ? (
            <>
              AI will draft slides that introduce <strong>{focusLessonName}</strong> without repeating earlier
              presentations or exercises. Add extra guidance if you want to steer topics.
            </>
          ) : (
            <>
              Create an empty deck, or generate slides from this spot in the course plan. The first
              presentation in a course usually welcomes students and previews the first exercise.
            </>
          )}
        </p>

        {phase === "ai" && aiProgress ? (
          <LessonAiProgressPanel mode="presentation-generate" progress={aiProgress} />
        ) : (
          <>
            <div className="teacher-field">
              <label htmlFor="add-presentation-title" className="teacher-field-label">
                Title (optional)
              </label>
              <input
                id="add-presentation-title"
                className="teacher-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={suggestedTitle}
                disabled={busy}
              />
              <p className="teacher-field-hint">
                Leave blank to use <strong>{suggestedTitle}</strong>.
              </p>
            </div>

            <div className="teacher-field">
              <label htmlFor="add-presentation-description" className="teacher-field-label">
                Description (optional)
              </label>
              <textarea
                id="add-presentation-description"
                className="teacher-input"
                rows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Short note for teachers"
                disabled={busy}
              />
            </div>

            <div className="teacher-quiz-ai-section">
              <div className="teacher-field">
                <label htmlFor="add-presentation-guidance" className="teacher-field-label">
                  Additional guidance (optional)
                </label>
                <textarea
                  id="add-presentation-guidance"
                  className="teacher-input"
                  rows={3}
                  value={guidance}
                  onChange={(e) => setGuidance(e.target.value)}
                  placeholder="e.g. Keep it to 8 slides and include a live demo of print()"
                  disabled={busy}
                />
              </div>
              <p className="muted small teacher-quiz-ai-hint">
                Generation already knows where this sits in the plan, what previous presentations covered, and
                what is new in the next exercise. Use this box only to add extra direction.
              </p>
            </div>

            {context === "template" && injectionLoading ? (
              <p className="muted small teacher-quiz-injection-hint">Checking active cohorts…</p>
            ) : null}

            {context === "template" && !injectionLoading && injectionCandidates.length > 0 ? (
              <div className="teacher-quiz-injection-panel" role="group" aria-labelledby="add-presentation-injection-title">
                <p id="add-presentation-injection-title" className="teacher-field-label">
                  Add to active cohorts
                </p>
                <p className="muted small teacher-quiz-injection-lede">
                  You have <strong>{injectionCandidates.length}</strong> active cohort
                  {injectionCandidates.length === 1 ? "" : "s"} created from this course. Add this
                  presentation to their lesson plans too?
                </p>
                <label className="teacher-quiz-injection-row teacher-quiz-injection-row--master">
                  <input
                    type="checkbox"
                    checked={injectionAllSelected}
                    onChange={(e) => toggleAllCohortInjection(e.target.checked)}
                    disabled={busy}
                  />
                  <span>Select all</span>
                </label>
                <ul className="teacher-quiz-injection-list">
                  {injectionCandidates.map((candidate) => (
                    <li key={candidate.cohortId}>
                      <label className="teacher-quiz-injection-row">
                        <input
                          type="checkbox"
                          checked={selectedCohortIds.has(candidate.cohortId)}
                          onChange={(e) => toggleCohortInjection(candidate.cohortId, e.target.checked)}
                          disabled={busy}
                        />
                        <span>{candidate.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}

        {phase === "ai" ? (
          <div className="teacher-modal-actions">
            <button type="button" className="btn secondary" onClick={cancelGeneration}>
              Cancel
            </button>
          </div>
        ) : null}

        {phase === "form" ? (
          <div className="teacher-modal-actions teacher-modal-actions--split">
            <button type="button" className="btn secondary" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <div className="teacher-modal-actions-primary">
              <button
                type="button"
                className="btn secondary"
                disabled={busy}
                onClick={() => void handleCreateEmpty()}
              >
                {busy ? "Creating…" : "Create empty deck"}
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={busy}
                title={AI_SPEND_TOOLTIP}
                onClick={() => void handleGenerate()}
              >
                <AiSpendIcon /> Generate with AI
              </button>
            </div>
          </div>
        ) : null}
      </form>
    </dialog>
  );
}
